import { PageHeader } from '@src/components/shared/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@src/components/ui/card';
import {
  computeCoverageSegments,
  COVERAGE_SEGMENT_KEYS,
  coverageSegmentLabel,
  FALLBACK_OUTCOME_COLOR,
  OUTCOME_COLOR,
} from '@src/lib/coverage';
import { errorMessage } from '@src/lib/errors';
import { cn } from '@src/lib/utils';
import { resources } from '@src/resources';
import { fetchCoverage, scopeLabel } from '@src/services/opsService';
import { useQuery } from '@tanstack/react-query';

/** Segment widths below this are too narrow to hold a readable inline "%" label. */
const LABEL_VISIBILITY_THRESHOLD_PCT = 12;

export function Coverage() {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['ops', 'coverage'],
    queryFn: fetchCoverage,
  });

  return (
    <div>
      <PageHeader title={resources.pages.coverage.title} subtitle={resources.pages.coverage.subtitle} />

      <Card>
        <CardHeader>
          <CardTitle>{resources.coverage.cardTitle}</CardTitle>
          <CardDescription>{resources.coverage.cardSubtitle}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-5 flex flex-wrap gap-4">
            {COVERAGE_SEGMENT_KEYS.map((key) => (
              <div key={key} className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
                <span
                  className={cn('inline-block h-2.5 w-2.5 rounded-sm', OUTCOME_COLOR[key] ?? FALLBACK_OUTCOME_COLOR)}
                />
                {coverageSegmentLabel(key)}
              </div>
            ))}
          </div>

          {isPending && <p className="text-sm text-muted-foreground">{resources.common.loading}</p>}
          {isError && (
            <div className="text-sm text-destructive">
              <p>{resources.coverage.loadError}</p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">{errorMessage(error)}</p>
            </div>
          )}

          {data && (
            <div className="flex flex-col gap-3">
              {data.items.map((row) => {
                const segments = computeCoverageSegments(row).filter((segment) => segment.count > 0);
                return (
                  <div key={row.scope} className="flex items-center gap-3">
                    <div className="w-40 flex-shrink-0 truncate text-sm text-foreground">
                      {scopeLabel(row.scope)}
                    </div>

                    <div className="flex h-4 flex-1 gap-0.5 overflow-hidden rounded-sm bg-muted">
                      {segments.map((segment) => (
                        <div
                          key={segment.outcome}
                          className={cn(
                            'flex items-center justify-center',
                            OUTCOME_COLOR[segment.outcome] ?? FALLBACK_OUTCOME_COLOR,
                          )}
                          style={{ flexGrow: segment.pct, flexBasis: 0 }}
                          title={`${segment.label}: ${segment.count.toLocaleString()} (${Math.round(segment.pct)}%)`}
                        >
                          {segment.pct >= LABEL_VISIBILITY_THRESHOLD_PCT && (
                            <span className="text-[9.5px] font-semibold text-white/90">
                              {Math.round(segment.pct)}%
                            </span>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="w-28 flex-shrink-0 text-right text-[11.5px] tabular-nums text-muted-foreground">
                      {row.totalIngested.toLocaleString()} {resources.coverage.ingestedSuffix}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <p className="mt-5 border-t border-border pt-3 text-[11px] text-muted-foreground">
            {resources.coverage.footnote}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
